import { FC, useEffect, useRef } from 'react';
import { useTableStoreValue } from '../../../tanstacktable';
import { emitFocusFormInput, listenFocusFormInput } from '../../events';
import { useFormTableName } from './TanstackFormTable';

export const AutoScrollToFocusedRow: FC = () => {
  const blockScrollRef = useRef(false);
  const focusNameRef = useRef<string | null>(null);
  const tableName = useFormTableName();
  const [virtualizer] = useTableStoreValue('virtualizer');
  const [tbody] = useTableStoreValue('tbody');

  useEffect(() => {
    if (!virtualizer || !tbody) {
      return;
    }

    const tableFieldNamePattern = new RegExp(
      `^${tableName}.([0-9]+).[^.0-9]+$`,
    );

    // wait for the scroll to happen and consequently the row to be rendered, then re-emit the focus event
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach(({ addedNodes }) => {
        addedNodes.forEach((node) => {
          if (node instanceof HTMLTableRowElement && focusNameRef.current) {
            const name = focusNameRef.current;
            focusNameRef.current = null;
            blockScrollRef.current = true;
            emitFocusFormInput(name, true);
          }
        });
      });
    });
    mutationObserver.observe(tbody, { childList: true, subtree: false });

    const removeListener = listenFocusFormInput((event) => {
      if (blockScrollRef.current) {
        blockScrollRef.current = false;
        return;
      }

      const { name } = event.detail;
      const match = name.match(tableFieldNamePattern);
      const focusRowIndex = match && match[1] ? Number(match[1]) : null;

      if (focusRowIndex !== null) {
        focusNameRef.current = name;
        const offset = virtualizer.getOffsetForIndex(focusRowIndex, 'end');
        if (offset) {
          virtualizer.scrollToOffset(offset[0]);
        }
      }
    });

    return () => {
      removeListener();
      mutationObserver.disconnect();
    };
  }, [tableName, tbody, virtualizer]);

  return null;
};
